import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useClientStore } from '@/stores/ClientStore'
import Query from '@/classes/queries/Query'
import i18n from '@/vendors/i18n'
import { whenever } from '@/utils/utils'
import { get as _get } from 'lodash'

export const useCurrencyStore = defineStore('CurrencyStore', () => {
  const formatters = ref({})
  const selected_currency = ref(localStorage.currency)

  const { client } = storeToRefs(useClientStore())

  const defaultCurrency = computed(() => _get(client.value, 'item.default_currency'))
  const activeCurrencies = computed(() => _get(client.value, 'item.active_currencies', []))
  const isMultiCurrency = computed(() => activeCurrencies.value.length > 1)

  const currency = computed({
    get: () => selected_currency.value,
    set: (currency = null) => selected_currency.value = localStorage.currency = getIfAvailable(currency) || getIfAvailable(localStorage.currency) || defaultCurrency.value
  })

  const getThousandsSeparator = (curr = currency.value) => i18n.t(`currencies.${curr.toLowerCase()}_thousands`) || ','

  const getDecimalsSeparator = (curr = currency.value) => i18n.t(`currencies.${curr.toLowerCase()}_decimals`) || '.'

  const getSymbol = (curr = currency.value) => getNumberFormater({ options: { currency: curr, style: 'currency', currencyDisplay: 'narrowSymbol' } })
    .formatToParts(0)
    .filter(part => part.type === 'currency')
    .map(({ value }) => value)
    .join('')

  const getNumberFormater = ({ options = {} } = {}) => {
    const locale = i18n.locale
    const key = new Query({ query: { ...options, locale } }).to_string

    return formatters.value[key] ?? (formatters.value[key] = new Intl.NumberFormat(locale, options))
  }

  const getFormatedNumber = (number = 0, options = {}) => {
    return getNumberFormater({ options: { currency: currency.value, style: 'decimal', currencyDisplay: 'narrowSymbol', minimumFractionDigits: number % 1 === 0 ? 0 : 2, maximumFractionDigits: 2, ...options } })
      .formatToParts(number)
      .map(({ type, value }) => {
        if (type === 'group') return getThousandsSeparator(options.currency)
        if (type === 'decimal') return getDecimalsSeparator(options.currency)
        return value
      })
      .join('')
  }

  const getIfAvailable = curr => currency.value && activeCurrencies.value.includes(curr) ? curr : null

  whenever(activeCurrencies, () => {
    if (!currency.value || !getIfAvailable(currency.value)) currency.value = null
  })

  return {
    currency,
    defaultCurrency,
    activeCurrencies,
    isMultiCurrency,
    getSymbol,
    getThousandsSeparator,
    getDecimalsSeparator,
    getFormatedNumber
  }
})